import axios from 'axios';

// Create an Axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.example.com', // Use .env for dynamic base URL
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // <-- Add this line
});

/** Reads a cookie value from document.cookie by name. */
function getCookieValue(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Request interceptor: attach CSRF token + light debug logging
api.interceptors.request.use(
  (config) => {
    // CSRF protection: read the XSRF-TOKEN cookie set by Spring Security's
    // CookieCsrfTokenRepository and forward it as X-XSRF-TOKEN on all
    // state-changing requests. GET/HEAD/OPTIONS are inherently safe and exempted.
    const method = config.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCookieValue('XSRF-TOKEN');
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken;
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('Making API request:', {
        url: config.url,
        method: config.method,
        baseURL: config.baseURL,
      });
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('API response received:', {
        url: response.config.url,
        status: response.status,
      });
    }
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('API request failed:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
      });
    }
    
    // Handle 401 Unauthorized — session expired or cookie invalid.
    // Redirect to login so the user can re-authenticate.
    // Exclusions:
    //   - Auth endpoints (/api/auth/*) — 401 is their expected response for
    //     unauthenticated users and wrong credentials; do NOT redirect here.
    //   - Already on the login page — prevents infinite redirect loops.
    if (error.response && error.response.status === 401) {
      const requestUrl: string = error.config?.url || '';
      const currentPath = window.location.pathname;

      // Do NOT redirect when the request is already an auth endpoint (401 is
      // the expected unauthenticated response — redirect would be a loop).
      const isAuthEndpoint = requestUrl.includes('/api/auth/');

      // Do NOT redirect when the user is already on a public page.
      // Public pages must be accessible without authentication, so a 401 from
      // any background call must never forcibly send the visitor to the login page.
      const isPublicPage = ['/', '/auth-pages/login', '/auth-pages/sign-up', '/auth-pages/404',
                             '/reporter-delete-account', '/reporter-privacy-policy',
                             '/contact-us'].includes(currentPath);

      if (!isAuthEndpoint && !isPublicPage) {
        console.log('Session expired - redirecting to login');
        window.location.href = '/auth-pages/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to upload files and return their paths from the API response
// Enhanced with post-type support for validation and specialized storage
export const uploadFile = async (
  file: File, 
  postType?: string, 
  fileCategory?: 'file' | 'thumbnail'
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  
  // Add post type for backend validation and storage routing
  if (postType) {
    formData.append('postType', postType);
  }
  
  // Add file category for better organization
  if (fileCategory) {
    formData.append('fileCategory', fileCategory);
  }

  const response = await api.post('/api/articles/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
    timeout: 300000, // 5 minutes for large video uploads
  });

  return response.data.filePath; // Assuming the API returns the file path in 'filePath'
};

// Helper function to convert file path to full URL
export const getFileUrl = (filePath: string): string => {
  if (!filePath) return '';
  // If it's already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  // Remove trailing slashes from baseURL and ALL leading slashes from filePath
  const baseURL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081').replace(/\/+$/, '');
  const cleanPath = filePath.replace(/^\/+/, '').replace(/^\/+/, '');
  // Ensure exactly one slash between baseURL and cleanPath
  return `${baseURL}/${cleanPath}`;
};

export default api;